import React, { useEffect, useState } from "react";
import {
  FaEnvelope,
  FaEnvelopeOpen,
  FaReply,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa";

import {
  buildMailtoUrl,
  formatDateTime,
  getMessageDate,
  getMessageId,
} from "../utils/messagesSectionUtils";

const TRANSITION_MS = 260;

export default function MessageDetailDrawer({
  isOpen,
  message,
  onClose,
  onToggleRead,
  onDelete,
}) {
  const [mounted, setMounted] = useState(isOpen);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => setShown(true));
      return;
    }

    setShown(false);

    const timeout = setTimeout(() => setMounted(false), TRANSITION_MS);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  if (!mounted || !message) return null;

  const messageId = getMessageId(message);
  const isRead = Boolean(message.read_message);

  return (
    <div className="fixed inset-0 z-[90]">
      <button
        type="button"
        className={[
          "absolute inset-0 bg-slate-950/55 backdrop-blur-sm transition-opacity duration-300",
          shown ? "opacity-100" : "opacity-0",
        ].join(" ")}
        onClick={onClose}
        aria-label="Fermer le panneau"
      />

      <aside
        className={[
          "absolute right-0 top-0 h-full w-full max-w-2xl transform overflow-hidden border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 dark:border-white/10 dark:bg-slate-950",
          shown ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-full flex-col">
          <div className="border-b border-slate-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-slate-950 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Message reçu
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Détail du message
                </h2>

                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {formatDateTime(getMessageDate(message))}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
              <div className="flex items-start gap-3">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <FaUser />
                </span>

                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                    {message.name || "Nom non renseigné"}
                  </h3>

                  {message.email && (
                    <a
                      href={buildMailtoUrl(message)}
                      className="mt-1 block text-sm font-bold text-primary hover:underline"
                    >
                      {message.email}
                    </a>
                  )}

                  <span
                    className={[
                      "mt-3 inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
                      isRead
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                        : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
                    ].join(" ")}
                  >
                    {isRead ? "Message lu" : "Message non lu"}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-[24px] border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Message
              </p>

              <p className="whitespace-pre-line text-sm leading-7 text-slate-700 dark:text-slate-200">
                {message.message || "Aucun contenu."}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={() => onToggleRead(messageId)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
            >
              {isRead ? <FaEnvelope /> : <FaEnvelopeOpen />}
              {isRead ? "Marquer non lu" : "Marquer lu"}
            </button>

            {message.email && (
              <a
                href={buildMailtoUrl(message)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
              >
                <FaReply />
                Répondre
              </a>
            )}

            <button
              type="button"
              onClick={() => onDelete(messageId)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
            >
              <FaTrash />
              Supprimer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}